import type { Address, Hex } from 'viem'

import { uint256StringSchema } from '@repo/utils'
import { isAddress } from 'viem'
import { z } from 'zod'

export { ORACLE_DECODING_OUTCOMES, ORACLE_METHODS } from '../shared/oracleDecoding'

/**
 * Shared input and output schemas. Contracts import these instead of defining their own,
 * so the same value is validated the same way everywhere.
 */

/** A positive integer chain id; procedures check it against the app's available chains. */
export const chainIdSchema = z.number().int().positive()

/** A 0x-prefixed 20-byte address (any casing). */
export const addressSchema = z.custom<Address>(
  value => typeof value === 'string' && isAddress(value, { strict: false }),
  'Invalid address'
)

/**
 * A 0x-prefixed 32-byte id (market id, cap id), typed as viem's `Hex`. Validate-only like
 * `addressSchema`, so a `@repo/cache` unit can key on it.
 */
export const hexIdSchema = z.custom<Hex>(
  value => typeof value === 'string' && /^0x[0-9a-fA-F]{64}$/.test(value),
  'Invalid 32-byte id'
)

/** A `<chainId>:<address>` asset selector, as the bridge's asset filters and facets carry it. */
export const assetSelectorSchema = z.custom<`${number}:0x${string}`>(
  value => typeof value === 'string' && /^\d+:0x[0-9a-fA-F]{40}$/.test(value),
  'Invalid asset selector'
)

/** A four-byte function selector. */
export const selectorSchema = z.custom<Hex>(
  value => typeof value === 'string' && /^0x[0-9a-f]{8}$/i.test(value),
  'Invalid selector'
)

/** Even-length, 0x-prefixed hex data. */
export const hexDataSchema = z.custom<Hex>(
  value => typeof value === 'string' && /^0x([0-9a-f]{2})*$/i.test(value),
  'Invalid hex data'
)

/** An opaque pagination cursor, bounded like the upstream `/v0` routes and markets-app. */
export const cursorSchema = z.string().min(1).max(4096)

/** Re-exported so contracts keep importing every schema from this module (Vaults app ESLint rule). */
export { uint256StringSchema }

/**
 * Output of a list procedure. `partial` is true when upstream rows failed validation and
 * were dropped, so the UI can say the list is incomplete instead of silently trimming it.
 */
export function listOutput<T extends z.ZodType>(item: T) {
  return z.strictObject({ items: z.array(item), partial: z.boolean() })
}

/** Output of a cursor-paginated list procedure: `listOutput` plus the next cursor. */
export function pageOutput<T extends z.ZodType>(item: T) {
  return z.strictObject({
    items: z.array(item),
    partial: z.boolean(),
    nextCursor: cursorSchema.nullable()
  })
}

/**
 * Output of a feed read whole up to a cap: `listOutput` plus `truncated`, true when the upstream
 * still had rows past the cap.
 */
export function feedOutput<T extends z.ZodType>(item: T) {
  return z.strictObject({ items: z.array(item), partial: z.boolean(), truncated: z.boolean() })
}

/**
 * What an address is known to be: a Safe (with its quorum) or an Aragon DAO. Shaped like
 * `ApiMetadataType` in `helpers/quorum.ts`, so `getSafeQuorum` and `hasAragonMetadata` read it.
 */
export const addressMetadataSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('safe'),
    owners: z.array(addressSchema),
    threshold: z.number().int().positive()
  }),
  z.strictObject({ kind: z.literal('aragon') })
])

export type AddressMetadata = z.output<typeof addressMetadataSchema>

/** A chart range preset: 1 week, 1 month, 3 months, all time. */
export const historyRangeSchema = z.enum(['1w', '1m', '3m', 'all'])

/** A token as a label: Core may know a token and have no metadata for it, hence the nulls. */
export const tokenLabelSchema = z.strictObject({
  address: addressSchema,
  symbol: z.string().nullable(),
  decimals: z.number().int().nullable(),
  logoUri: z.string().nullable()
})

/**
 * A Blue market as a label. `loanToken` and `lltv` are null for a market Core does not serve;
 * `collateralToken` is null then too, and for an idle market, which has no collateral.
 */
export const marketLabelSchema = z.strictObject({
  marketId: hexIdSchema,
  loanToken: tokenLabelSchema.nullable(),
  collateralToken: tokenLabelSchema.nullable(),
  lltv: uint256StringSchema.nullable()
})

/** A market's loan or collateral token with its USD price, null when Core has none. */
export const pricedTokenSchema = z.strictObject({
  address: addressSchema,
  symbol: z.string().nullable(),
  decimals: z.number().int().nonnegative(),
  logoUri: z.string().nullable(),
  /** Null when Core cannot price the token; never 0. */
  priceUsd: z.number().nonnegative().nullable()
})

/**
 * One reward token a direct position on a market side earns, with its rewards-only APR as a
 * fraction (0.05 is 5%). `merklOpportunityUrl` is null when the bridge has no campaign page for it.
 */
export const marketApyRewardSchema = z.strictObject({
  token: z.strictObject({
    chainId: chainIdSchema,
    address: addressSchema,
    symbol: z.string().nullable(),
    logoUri: z.string().nullable()
  }),
  apr: z.number(),
  merklOpportunityUrl: z.string().nullable()
})

/**
 * One active market warning. `type` and `level` pass through as the bridge sends them, so a type
 * the Vaults app does not render yet still reaches the client. `content` is set on `custom` warnings only,
 * the bad-debt amounts on `bad_debt_unrealized` only.
 */
export const marketWarningSchema = z.strictObject({
  type: z.string(),
  level: z.string(),
  content: z.string().nullable(),
  badDebtAssets: uint256StringSchema.nullable(),
  badDebtUsd: z.number().nullable()
})

/**
 * One reward token a vault pays, with its rewards-only supply APR as a fraction (0.05 is 5%) and
 * the Merkl campaign it comes from; `merklOpportunityUrl` is null for a reward forwarded from a
 * market rather than paid by a campaign on the vault.
 */
export const vaultApyRewardSchema = z.strictObject({
  token: z.strictObject({
    chainId: chainIdSchema,
    address: addressSchema,
    symbol: z.string().nullable(),
    logoUri: z.string().nullable()
  }),
  supplyApr: z.number(),
  merklOpportunityUrl: z.string().nullable()
})

/** One point of a vault's net APY chart, rewards included; null where the bridge has no APY. */
export const vaultApyHistoryPointSchema = z.strictObject({
  timestamp: z.number().int().nonnegative(),
  netApy: z.number().nullable()
})

/** Net APY over a range, rewards included; `averageNetApy` is null when history is too short. */
export const vaultApyHistorySchema = z.strictObject({
  averageNetApy: z.number().nullable(),
  points: z.array(vaultApyHistoryPointSchema)
})

/** A curator of a vault: an owner or curator address of the vault is registered to it. */
export const vaultCuratorSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  image: z.string().nullable(),
  verified: z.boolean(),
  aumUsd: z.number().nullable()
})

/**
 * A vault role's address and what is known about it: a Safe's owners and threshold, or that it is
 * an Aragon DAO. `metadata` is empty when nothing is.
 */
export const vaultRoleSchema = z.strictObject({
  address: addressSchema,
  metadata: z.array(addressMetadataSchema)
})

/** The Vault V2 functions behind its timelock, by ABI name. */
export const TIMELOCKED_FUNCTIONS = [
  'setIsAllocator',
  'setReceiveSharesGate',
  'setSendSharesGate',
  'setReceiveAssetsGate',
  'setSendAssetsGate',
  'setAdapterRegistry',
  'addAdapter',
  'removeAdapter',
  'increaseTimelock',
  'decreaseTimelock',
  'setPerformanceFee',
  'setManagementFee',
  'setPerformanceFeeRecipient',
  'setManagementFeeRecipient',
  'increaseAbsoluteCap',
  'increaseRelativeCap',
  'setForceDeallocatePenalty',
  'abdicate'
] as const

/** A V2 vault's native APY split into organic yield and surplus boost; null for V1 vaults. */
export const vaultV2ApyBreakdownSchema = z
  .strictObject({
    apy: z.number(),
    organicApy: z.number(),
    surplusBoostApy: z.number(),
    netApyExcludingRewards: z.number().nullable(),
    organicNetApyExcludingRewards: z.number().nullable(),
    surplusBoostNetApyExcludingRewards: z.number().nullable(),
    surplusBoostActive: z.boolean()
  })
  .nullable()
